import {calculateRandomValueWithPercentage, getRandomNrBetween} from './util/random-numbers'
import {ChangeableParticleAttributes, Vector} from './types'
import {SoundEngine} from './sound/sound-engine'
import {defaultGravityMultiplier, defaultTimeScaleFactor} from './constants-defaults'
import {Dimensions} from './renderers/canvas-renderer'
import {Grid} from './grid/grid'
import {colorEqual, getColor, getRandomColorRgb, mixRgb, setColor} from './util/color'
import {DeviceMotionData, SensorService} from './sensors/sensor-service'
import {Renderer} from './renderers/renderer'

export type ParticleInfo = {
  particleCount: number,
  posX: Float32Array,
  posY: Float32Array
  radii: Float32Array
  colors: Float32Array
  alive: Float32Array
  stabilized: Float32Array
};

export class ParticleEngine {
  MAX_PARTICLES = 10000
  RING_BUFFER_LENGTH = 10
  currentParticleCount = 0

  posX: Float32Array = new Float32Array(this.MAX_PARTICLES)
  posY: Float32Array = new Float32Array(this.MAX_PARTICLES)
  speedX: Float32Array = new Float32Array(this.MAX_PARTICLES)
  speedY: Float32Array = new Float32Array(this.MAX_PARTICLES)
  radii: Float32Array = new Float32Array(this.MAX_PARTICLES)
  alive: Float32Array = new Float32Array(this.MAX_PARTICLES)
  stabilized: Float32Array = new Float32Array(this.MAX_PARTICLES)
  colors: Float32Array = new Float32Array(this.MAX_PARTICLES * 3)

  mass: Float32Array = new Float32Array(this.MAX_PARTICLES)
  bounciness: Float32Array = new Float32Array(this.MAX_PARTICLES)
  lastColorChange: Float32Array = new Float32Array(this.MAX_PARTICLES)
  lastStabilized: Float32Array = new Float32Array(this.MAX_PARTICLES)
  lifeSpan: Float32Array = new Float32Array(this.MAX_PARTICLES)
  spawnedAt: Float32Array = new Float32Array(this.MAX_PARTICLES)

  bufferCounter = 0
  speedXHistoryBuffer = Array.from({length: this.MAX_PARTICLES}, () => new Float32Array(this.RING_BUFFER_LENGTH))
  speedYHistoryBuffer = Array.from({length: this.MAX_PARTICLES}, () => new Float32Array(this.RING_BUFFER_LENGTH))
  posXHistoryBuffer = Array.from({length: this.MAX_PARTICLES}, () => new Float32Array(this.RING_BUFFER_LENGTH))
  posYHistoryBuffer = Array.from({length: this.MAX_PARTICLES}, () => new Float32Array(this.RING_BUFFER_LENGTH))
  collisionHistoryBuffer = Array.from({length: this.MAX_PARTICLES}, () => new Float32Array(this.RING_BUFFER_LENGTH))

  readonly GRAVITY = 0.002 // px/s²
  readonly STABILIZATION_COOLDOWN = 2000
  readonly IMPULSE_DAMP = 0.95
  private STABILIZED_CORRECTION_DAMP = 0.5
  private FRICTION_STABILIZED = 0.8
  private GRAVITY_STABILIZED = 0
  readonly WALL_BOUNCE = 0.5
  readonly MAX_DELTA_TIME = 100

  private resizeTimeout: number | undefined
  private currentDimensions: Dimensions
  private particleCounterElement

  public timeScaleFactor = defaultTimeScaleFactor
  public gravityMultiplier = defaultGravityMultiplier
  public colorTransferEnabled = false
  public colorTransferMode: 'MIX' | 'REPLACE' = 'REPLACE'
  public collisionsEnabled = true
  public useSensors = true

  public grid: Grid
  private colorChangeCooldown = 2000
  private ACCELERATION_DESTABILIZATION_THRESH = 10
  private pickedUpParticles: { particle: number, offset: Vector }[] = []



  constructor(readonly renderer: Renderer, readonly soundEngine: SoundEngine, readonly sensorService: SensorService) {
    setInterval(this.updateParticleCounter.bind(this), 1000)
    this.currentDimensions = renderer.getCurrentDimensions()

    this.grid = new Grid(this.currentDimensions)
    this.resetParticles()

    window.addEventListener('resize', () => {
      this.handleResize()
    })

    this.particleCounterElement = document.getElementById('particle-counter') as HTMLDivElement
  }

  public spawnParticles(options: ChangeableParticleAttributes, position?: Vector): void {
    const {height, width} = this.currentDimensions
    let toSpawn = options.nrOfParticles
    if (this.currentParticleCount + toSpawn > this.MAX_PARTICLES) {
      toSpawn = this.MAX_PARTICLES - this.currentParticleCount
    }
    const timeStamp = performance.now()
    const oldLength = this.currentParticleCount

    for (let i = 0, j = 0; i < toSpawn; i++, j += 3) {
      const radius = calculateRandomValueWithPercentage(options.radius.value, options.radius.randomness, 1, 40)
      const pos: Vector = position ? {
        x: position.x + getRandomNrBetween(-10, 10),
        y: position.y + getRandomNrBetween(-10, 10)
      } : {
        x: Math.floor(Math.random() * width),
        y: Math.floor(Math.random() * height / 4),
      }

      this.posX[oldLength + i] = pos.x
      this.posY[oldLength + i] = pos.y

      this.radii[oldLength + i] = radius

      const speed = {
        x: getRandomNrBetween(-0.250, 0.250),
        y: 0,
      }
      this.speedX[oldLength + i] = speed.x
      this.speedY[oldLength + i] = speed.y

      const [r, g, b] = getRandomColorRgb(options.color.value, options.color.randomness)
      this.colors[oldLength * 3 + j] = r
      this.colors[oldLength * 3 + j + 1] = g
      this.colors[oldLength * 3 + j + 2] = b

      this.alive[oldLength + i] = 1
      this.stabilized[oldLength + i] = 0
      
      this.mass[oldLength + i] = 10 * radius * radius * 3
      this.bounciness[oldLength + i] = calculateRandomValueWithPercentage(options.bounciness.value, options.bounciness.randomness, 0, 1)
      this.lastColorChange[oldLength + i] = timeStamp
      this.spawnedAt[oldLength + i] = timeStamp
      this.lifeSpan[oldLength + i] = calculateRandomValueWithPercentage(options.lifespan.value, options.lifespan.randomness, 1, 60) * 1000
    }
    this.updateCellsForParticles()

    this.currentParticleCount += toSpawn
  }

  public updateParticles(deltaTimeMs: number, timestamp: number): ParticleInfo {
    const deltaTime = Math.min(this.timeScaleFactor * deltaTimeMs, this.MAX_DELTA_TIME)
    const {height, width} = this.currentDimensions

    let gravityDirection: Vector = {x: 0, y: 1}
    let sensorData: DeviceMotionData | null | undefined

    if (this.useSensors) {
      sensorData = this.sensorService.getSensorData()
      if (sensorData &&
        sensorData.acceleration?.x &&
        sensorData.acceleration.y &&
        sensorData.accelerationIncludingGravity?.x
        && sensorData.accelerationIncludingGravity.y) {
        const gravityOnly: Vector = {
          x: sensorData.accelerationIncludingGravity.x - sensorData.acceleration.x,
          y: sensorData.accelerationIncludingGravity.y - sensorData.acceleration.y
        }
        const gravityMagnitude = Math.sqrt(gravityOnly.x * gravityOnly.x + gravityOnly.y * gravityOnly.y)
        const gravityNormalized = {x: gravityOnly.x / gravityMagnitude, y: gravityOnly.y / gravityMagnitude}
        gravityDirection = {x: gravityNormalized.x, y: gravityNormalized.y * -1}
      }

    }


    for (let p1Index = 0; p1Index < this.currentParticleCount; p1Index++) {

      this.posXHistoryBuffer[p1Index][this.bufferCounter] = this.posX[p1Index]
      this.posYHistoryBuffer[p1Index][this.bufferCounter] = this.posY[p1Index]
      this.speedXHistoryBuffer[p1Index][this.bufferCounter] = this.speedX[p1Index]
      this.speedYHistoryBuffer[p1Index][this.bufferCounter] = this.speedY[p1Index]


      if (!this.alive[p1Index] || this.pickedUpParticles.find(p => p.particle === p1Index)) {
        continue
      } else if (this.lifeSpan[p1Index] > 0 && timestamp > (this.spawnedAt[p1Index] + this.lifeSpan[p1Index])) {
        this.alive[p1Index] = 0
        continue
      }

      if (sensorData?.acceleration?.x && sensorData.acceleration.y) {
        this.speedX[p1Index] += this.GRAVITY * deltaTime * sensorData.acceleration.x
        this.speedY[p1Index] += -this.GRAVITY * deltaTime * sensorData.acceleration.y
      }

      if (this.stabilized[p1Index]) {
        this.speedX[p1Index] *= this.FRICTION_STABILIZED
        this.speedY[p1Index] *= this.FRICTION_STABILIZED

        if (Math.abs(this.speedX[p1Index]) < 0.01) this.speedX[p1Index] = 0
        if (Math.abs(this.speedY[p1Index]) < 0.01) this.speedY[p1Index] = 0
      }


      this.posX[p1Index] += this.speedX[p1Index] * deltaTime
      this.posY[p1Index] += this.speedY[p1Index] * deltaTime

      const gravityStabilizedMultiplier = this.stabilized[p1Index] ? this.GRAVITY_STABILIZED : 1
      // Fake air resistance
      const radiusMultiplier = this.radii[p1Index] * 0.1
      this.speedX[p1Index] += gravityDirection.x * this.GRAVITY * this.gravityMultiplier * gravityStabilizedMultiplier * radiusMultiplier * deltaTime
      this.speedY[p1Index] += gravityDirection.y * this.GRAVITY * this.gravityMultiplier * gravityStabilizedMultiplier * radiusMultiplier * deltaTime


      // Collisions with side walls, immediately resolved
      // collisions with bottom / top
      let wallCollision = false
      let wallCollisionType: CollisionType | undefined

      if (this.posY[p1Index] >= height - (this.radii[p1Index])) {
        this.posY[p1Index] = height - (this.radii[p1Index])
        this.speedY[p1Index] = this.speedY[p1Index] * this.bounciness[p1Index] * this.WALL_BOUNCE * -1
        wallCollision = true
        wallCollisionType = 'VERTICAL'
      } else if (this.posY[p1Index] <= this.radii[p1Index]) {
        // 2* prevents stuck particles at the top
        this.posY[p1Index] = 2 * this.radii[p1Index]
        this.speedY[p1Index] = this.speedY[p1Index] * this.bounciness[p1Index] * this.WALL_BOUNCE * -1
        wallCollision = true
        wallCollisionType = 'VERTICAL'
      }
      // collisions with side walls
      if (this.posX[p1Index] - this.radii[p1Index] <= 0) {
        this.posX[p1Index] = this.radii[p1Index]
        this.speedX[p1Index] = this.speedX[p1Index] * this.bounciness[p1Index] * this.WALL_BOUNCE * -1
        wallCollision = true
        wallCollisionType = 'HORIZONTAL'
      } else if (this.posX[p1Index] + this.radii[p1Index] >= width) {
        this.posX[p1Index] = width - this.radii[p1Index]
        this.speedX[p1Index] = this.speedX[p1Index] * this.bounciness[p1Index] * this.WALL_BOUNCE * -1
        wallCollision = true
        wallCollisionType = 'HORIZONTAL'
      }

      if (!this.collisionsEnabled) {
        continue
      }

      const collisions: number[] = []
      const cells = this.grid.cellsForParticle[p1Index]

      for (let k = 0; k < cells.length; k++) {
        const cell = cells[k]
        const particlesInCell = this.grid.particlesPerCell[cell]
        for (let j = 0; j < particlesInCell.length; j++) {
          const p2Index = particlesInCell[j]
          if (!this.alive[p2Index] || p2Index === p1Index) {
            continue
          }
          const radiiAdded = this.radii[p1Index] + this.radii[p2Index]
          const deltaX = this.posX[p2Index] - this.posX[p1Index]
          const deltaY = this.posY[p2Index] - this.posY[p1Index]

          const distanceSq = deltaX * deltaX + deltaY * deltaY

          if (distanceSq <= radiiAdded * radiiAdded) {
            // Collision

            collisions.push(p2Index)


          }
        }
      }

      const nrOfCollisions = collisions.length + (wallCollision ? 1 : 0);
      this.collisionHistoryBuffer[p1Index][this.bufferCounter] = nrOfCollisions
      if (this.soundEngine.soundEnabled && nrOfCollisions > 0) {
        this.handleCollisionSound(p1Index)
      }
      if (collisions.length === 0) {
        continue
      }

      if (this.colorTransferEnabled && timestamp > this.lastColorChange[p1Index] + this.colorChangeCooldown * Math.random()) {
        const transfer = Math.random() >= 0.9
        const transferringParticle = collisions.find(p => this.lastColorChange[p] + this.spawnedAt[p] > this.lastColorChange[p1Index] + this.spawnedAt[p1Index])
        if (transferringParticle && transfer) {
          const oldColor = getColor(this.colors,p1Index)
          const transferringColor = getColor(this.colors, transferringParticle)
          const newColor = this.colorTransferMode === 'MIX' ? mixRgb(oldColor, transferringColor) : transferringColor
          if (!colorEqual(newColor, oldColor)) {
            setColor(this.colors, p1Index, newColor)
            this.lastColorChange[p1Index] = timestamp
          }
        }
      }

      //Stabilization
      const speedHistoryX = this.speedXHistoryBuffer[p1Index]
      const speedHistoryY = this.speedYHistoryBuffer[p1Index]

      const collisionHistory = this.collisionHistoryBuffer[p1Index]
      const speedAndCollisionsStable = [speedHistoryX, speedHistoryY].every(history => history.every(s => Math.abs(s) * this.radii[p1Index] < 10)) && collisionHistory.every(c => c > 2)

      const supportedBelow = (wallCollision && wallCollisionType === 'VERTICAL') || collisions.some(p2 => {
        const proj1 = this.posX[p1Index] * gravityDirection.x + this.posY[p1Index] * gravityDirection.y
        const proj2 = this.posX[p2] * gravityDirection.x + this.posY[p2] * gravityDirection.y
        return proj2 > proj1
      })

      const totalAcceleration = Math.abs(sensorData?.acceleration?.x ?? 0) + Math.abs(sensorData?.acceleration?.y ?? 0) + Math.abs(sensorData?.acceleration?.z ?? 0)
      const accelerationBelowThresh = totalAcceleration < this.ACCELERATION_DESTABILIZATION_THRESH

      const stabilizationCooldownActive = !this.stabilized[p1Index] && (timestamp - (this.lastStabilized[p1Index] ?? 0) < this.STABILIZATION_COOLDOWN)
      this.stabilized[p1Index] = (
        (timestamp - this.spawnedAt[p1Index] > 1000)
        && speedAndCollisionsStable
        && supportedBelow
        && accelerationBelowThresh
        && !stabilizationCooldownActive) ?
        1 : 0

      for (let m = 0; m < collisions.length; m++) {
        const p2 = collisions[m]
        const p2Index = p2
        if (!this.alive[p2Index]) continue
        /**
         *  Position correction
         */
        const radiiAdded = this.radii[p1Index] + this.radii[p2Index]
        const deltaX = this.posX[p2Index] - this.posX[p1Index]
        const deltaY = this.posY[p2Index] - this.posY[p1Index]
        const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY)

        const overlap = radiiAdded - distance
        const OVERLAP_TOLERANCE = 0.01
        const minOverlapAllowed = OVERLAP_TOLERANCE * (this.radii[p1Index] > this.radii[p2Index] ? this.radii[p2Index] * 0.01 : this.radii[p1Index] * 0.01)

        if (overlap > minOverlapAllowed) {
          const normalX = deltaX / distance
          const normalY = deltaY / distance

          const stabilizationModifierP = (this.stabilized[p1Index] ? this.STABILIZED_CORRECTION_DAMP : 1)
          const stabilizationModifierOtherP = (this.stabilized[p2Index] ? this.STABILIZED_CORRECTION_DAMP : 1)

          const correctionX = normalX * overlap * 0.5
          const correctionY = normalY * overlap * 0.5

          // IDEA: position correction in batch
          this.posX[p1Index] -= correctionX * stabilizationModifierP
          this.posY[p1Index] -= correctionY * stabilizationModifierP

          this.posX[p2Index] += correctionX * stabilizationModifierOtherP
          this.posY[p2Index] += correctionY * stabilizationModifierOtherP

          /**
           *  Velocity transfer
           */

          const relativeVelX = this.speedX[p2Index] - this.speedX[p1Index]
          const relativeVelY = this.speedY[p2Index] - this.speedY[p1Index]

          const velocityAlongNormal = relativeVelX * normalX + relativeVelY * normalY

          if (velocityAlongNormal > 0) {
            continue
          }

          const restitution = (this.bounciness[p1Index] + this.bounciness[p2]) / 2

          const impulse = -(1 + restitution) * velocityAlongNormal / (1 / this.mass[p1Index] + 1 / this.mass[p2])

          const impulseX = impulse * normalX
          const impulseY = impulse * normalY

          const dampP1 = this.IMPULSE_DAMP
          const dampP2 = this.IMPULSE_DAMP

          this.speedX[p1Index] -= dampP1 * impulseX / this.mass[p1Index]
          this.speedY[p1Index] -= dampP1 * impulseY / this.mass[p1Index]
          this.speedX[p2Index] += dampP2 * impulseX / this.mass[p2]
          this.speedY[p2Index] += dampP2 * impulseY / this.mass[p2]
        }
      }
    }
    this.updateCellsForParticles()
    this.bufferCounter = (this.bufferCounter + 1) % this.RING_BUFFER_LENGTH
    return {
      particleCount: this.currentParticleCount,
      posX: this.posX,
      posY: this.posY,
      radii: this.radii,
      colors: this.colors,
      alive: this.alive,
      stabilized: this.stabilized
    }
  }

  public resetParticles() {

    this.posX = new Float32Array(this.MAX_PARTICLES)
    this.posY = new Float32Array(this.MAX_PARTICLES)
    this.speedX = new Float32Array(this.MAX_PARTICLES)
    this.speedY = new Float32Array(this.MAX_PARTICLES)
    this.radii = new Float32Array(this.MAX_PARTICLES)
    this.alive = new Float32Array(this.MAX_PARTICLES)
    this.colors = new Float32Array(this.MAX_PARTICLES * 3)

    this.stabilized = new Float32Array(this.MAX_PARTICLES)
    this.mass = new Float32Array(this.MAX_PARTICLES)

    this.bounciness = new Float32Array(this.MAX_PARTICLES)
    this.lastColorChange = new Float32Array(this.MAX_PARTICLES)
    this.lifeSpan = new Float32Array(this.MAX_PARTICLES)
    this.spawnedAt = new Float32Array(this.MAX_PARTICLES)

    this.currentParticleCount = 0
    this.grid.init(this.currentDimensions, this.MAX_PARTICLES)
  }

  public handleResize() {
    clearTimeout(this.resizeTimeout)
    this.resizeTimeout = setTimeout(() => {
      clearTimeout(this.resizeTimeout)
      const newDimensions = this.renderer.getCurrentDimensions()
      if (newDimensions.height !== this.currentDimensions.height || newDimensions.width !== this.currentDimensions.width) {
        this.currentDimensions = this.renderer.getCurrentDimensions()
        this.grid.init(this.currentDimensions, this.MAX_PARTICLES)
        this.updateCellsForParticles()
      }
    }, 100)
  }

  private updateParticleCounter() {
    const alive = this.alive.filter(x => x === 1).length
    this.particleCounterElement.innerText = `${alive}/${this.currentParticleCount} alive/total`
  }

  private updateCellsForParticles() {
    if (this.currentParticleCount === 0) {
      return
    }
    if (this.collisionsEnabled) {
      this.grid.updateCellsForParticles(this.radii, this.posX, this.posY)
    }
  }


private handleCollisionSound(p1: number) {
    const radius = this.radii[p1]
  const speedX = this.speedX[p1]
  const speedY = this.speedY[p1]
  const thresholdSound = 1
  const calculateVolume = (v: number) => {
    let volume = radius / 40 + v / 1000
    return Math.min(volume, 1)
  }

  const random = Math.random();
  const detuneFactor = random * 50 * (-radius) + random * 3000
  let totalSpeed = Math.abs(speedX) + Math.abs(speedY);
  if (totalSpeed > thresholdSound) {
    this.soundEngine.playSound('BALL_HIT', calculateVolume(totalSpeed), detuneFactor)
  }
}

  public pickUpParticles(position: Vector, pickupRadius: number) {
    const pickedUpParticles: typeof this.pickedUpParticles = []

    for (let i = 0; i < this.currentParticleCount; i++) {
      const x = this.posX[i]
      const y = this.posY[i]
      const deltaX = position.x - x
      const deltaY = position.y - y
      const distanceSq = deltaX * deltaX + deltaY * deltaY
      const radiiAdded = pickupRadius + this.radii[i]
      if (distanceSq <= radiiAdded * radiiAdded) {
        this.speedX[i] = 0
        this.speedY[i] = 0
        pickedUpParticles.push({particle: i, offset: {x: this.posX[i] - position.x, y: this.posY[i] - position.y}})
      }
    }
    this.pickedUpParticles = pickedUpParticles
  }

  public pickedUpParticlesMove(position: Vector) {
    for (let i = 0; i < this.pickedUpParticles.length; i++) {
      const p = this.pickedUpParticles[i]
      this.posX[p.particle] = position.x + p.offset.x
      this.posY[p.particle] = position.y + p.offset.y
    }
  }

  public releaseParticles() {
    for (let i = 0; i < this.pickedUpParticles.length; i++) {
      const p = this.pickedUpParticles[i]
      // const lastPositions = p.particle.history.pos.get()
      // const maxSpeed = 50
      //
      // const avgSpeed: Vector = {x: 0, y: 0}
      // for (let i = 0; i < lastPositions.length - 2; i++) {
      //   const position = lastPositions[i]
      //   const positionBefore = lastPositions[i + 1]
      //   avgSpeed.x += Math.min((position.x - positionBefore.x) / this.lastDeltaTime, maxSpeed)
      //   avgSpeed.y += Math.min((position.y - positionBefore.y) / this.lastDeltaTime, maxSpeed)
      // }
      //
      // avgSpeed.x /= lastPositions.length
      // avgSpeed.y /= lastPositions.length

      this.speedX[p.particle] = 0
      this.speedY[p.particle] = 0
    }
    this.pickedUpParticles = []
  }

}

type CollisionType = 'VERTICAL' | 'HORIZONTAL' | 'PARTICLES'
